import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAuthState } from 'store/authslice'

interface ToastProps {
  message: JSX.Element | string
  correlationId?: string
}

const CopyableCorrelationId = ({ correlationId }: { correlationId: string }): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const { user } = useSelector(selectAuthState)
  const isAdmin = user.userRoles.some((role) => role === 'Handläggare' || role === 'SiteAdmin')

  const handleCopy = () => {
    navigator.clipboard.writeText(correlationId).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="d-flex align-items-center gap-2 mt-1">
      <small className="opacity-75">
        Fel-ID: <strong>{correlationId}</strong>
      </small>
      {isAdmin && (
        <button
          type="button"
          onClick={handleCopy}
          className="btn btn-sm p-0 px-1 text-white opacity-75 border-0"
          style={{ fontSize: '0.7rem', lineHeight: 1.2 }}
          title="Kopiera Fel-ID"
        >
          {copied ? '✓ Kopierat' : 'Kopiera'}
        </button>
      )}
    </div>
  )
}

export const ToastSuccess = ({ message }: ToastProps): JSX.Element => {
  return <span>{message}</span>
}

export const getSuccessToast = ({ message }: ToastProps): JSX.Element => <ToastSuccess message={message} />

export const ToastWarning = ({ message }: ToastProps): JSX.Element => {
  return <span>{message} </span>
}

export const getWarningToast = ({ message }: ToastProps): JSX.Element => <ToastWarning message={message} />

export const ToastError = ({ message, correlationId }: ToastProps): JSX.Element => {
  return (
    <div className="fill-white d-flex flex-column">
      <span>{message}</span>
      {correlationId && <CopyableCorrelationId correlationId={correlationId} />}
    </div>
  )
}

export const getErrorToast = ({ message, correlationId }: ToastProps): JSX.Element => (
  <ToastError message={message} correlationId={correlationId} />
)

export const dispatchToastPersistentSuccess = ({ message }: ToastProps): void => {
  toast.success(getSuccessToast({ message: message ?? 'Sparad' }), {
    autoClose: false,
    closeButton: true,
    containerId: 'pushToastContainer',
    position: toast.POSITION.TOP_CENTER,
  })
}

export const dispatchToastSuccess = ({ message }: ToastProps): void => {
  toast.success(getSuccessToast({ message: message ?? 'Sparad' }), {
    theme: 'colored',
    pauseOnHover: false,
    autoClose: 3000,
    closeButton: false,
    containerId: 'pushToastContainer',
    position: toast.POSITION.BOTTOM_CENTER,
  })
}

export const dispatchToastError = ({ message, correlationId }: ToastProps): void => {
  toast.error(getErrorToast({ message: message ?? 'Fel', correlationId }), {
    theme: 'colored',
    autoClose: false,
    closeButton: true,
    containerId: 'pushToastContainer',
    position: toast.POSITION.BOTTOM_CENTER,
  })
}

export const dispatchToastPersistentWarning = ({ message }: ToastProps): void => {
  toast.warn(getWarningToast({ message: message ?? 'Varning' }), {
    theme: 'colored',
    autoClose: false,
    closeButton: true,
    containerId: 'pushToastContainer',
    position: toast.POSITION.TOP_CENTER,
  })
}

export const dispatchToastWarning = ({ message }: ToastProps): void => {
  toast.warn(getWarningToast({ message: message ?? 'Fel' }), {
    autoClose: false,
    closeButton: true,
    containerId: 'pushToastContainer',
    position: toast.POSITION.TOP_CENTER,
  })
}

export const dismiss = (): void => {
  toast.dismiss()
}
